import type { Document } from "./types";

const FORBIDDEN_FOLDER_CHARACTERS_RE = /[\\<>:"|?*\x00-\x1f]/;

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatFileDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

export function fileExtension(fileName: string): string {
  return fileName.split(".").pop()?.slice(0, 3).toUpperCase() ?? "DOC";
}

export function normalizeFolderPath(raw: string): string | null {
  let path = raw.trim();
  if (path === "" || path === "/") return "/";
  if (!path.startsWith("/")) path = `/${path}`;
  if (!path.endsWith("/")) path = `${path}/`;
  path = path.replace(/\/{2,}/g, "/");
  if (FORBIDDEN_FOLDER_CHARACTERS_RE.test(path)) return null;
  return path;
}

export function folderName(path: string): string {
  return path === "/" ? "Root" : path.split("/").filter(Boolean).at(-1) ?? path;
}

export function folderDepth(path: string): number {
  return path.split("/").filter(Boolean).length;
}

export function folderPaths(documents: readonly Document[]): string[] {
  const paths = new Set<string>(["/"]);
  for (const document of documents) {
    let path = "/";
    for (const segment of document.folderPath.split("/").filter(Boolean)) {
      path += `${segment}/`;
      paths.add(path);
    }
  }
  return [...paths].sort((left, right) => left.localeCompare(right));
}

export function immediateChildFolders(paths: readonly string[], parent: string): string[] {
  return paths.filter((path) => {
    if (path === parent || !path.startsWith(parent)) return false;
    const remainder = path.slice(parent.length);
    return remainder.split("/").filter(Boolean).length === 1;
  });
}

export function documentsBelow(documents: readonly Document[], folderPath: string): number {
  return documents.filter((document) => document.folderPath.startsWith(folderPath)).length;
}

export function appendRelativeFolder(base: string, relativePath: string): string | null {
  const relative = relativePath.replace(/^\/+/, "");
  return normalizeFolderPath(`${base}${relative}`);
}
